import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router';

import { useCompleteLogin, useInitLogin } from 'apps-common/hooks/AccountApi';
import { identify } from 'apps-common/utils/analytics';
import { throwError } from 'apps-common/utils/errorHandler';
import { logger } from 'apps-common/utils/logger';
import { Loader } from 'ui';

import { routes } from '../routes';
import { useStore } from '../store';

interface LoginFormProps {
  onLogin: () => void;
}

const MoiLogin = ({ onLogin }: LoginFormProps) => {
  const navigate = useNavigate();
  // initial login landing and redirected page back to login from MOI
  const [searchParams] = useSearchParams();
  logger.info('Moi login started', { auth: 'moi' });
  const error = searchParams.get('error') ?? undefined;
  const errorDescription = searchParams.get('error_description') ?? undefined;
  const setAccountId = useStore((state) => state.setAccountId);

  if (error) {
    logger.error('Moi login failed', {
      auth: 'moi',
      reason: 'Error in login: ' + error + ' : ' + errorDescription,
    });
    throwError('moiLoginError', errorDescription);
  }
  const redirectUri = window.location.href.split('?')[0];
  const state = searchParams.get('state') ?? '';
  const code = searchParams.get('code') ?? '';
  const nonce = searchParams.get('nonce') ?? undefined;
  // redirects user to MOI page where entering user account email + one-time code from email
  const { data: initData, isFetching } = useInitLogin(redirectUri, !(state && code), nonce);

  // once user enters one-time code, completes login process by exchanging the code and state for an access token
  const { data: endLoginData, isFetching: isFetchingLoginEnd } = useCompleteLogin(
    redirectUri,
    code,
    state,
    state !== '' && code !== '',
  );

  useEffect(() => {
    if (endLoginData) {
      setAccountId(endLoginData.accountId);
      identify({ userId: endLoginData.analyticsId, traits: { isHipaa: endLoginData.isHipaa } });
      onLogin();
      logger.info('Moi login completed', { auth: 'moi' });
      void navigate(routes.index);
    }
  }, [endLoginData, onLogin, setAccountId, navigate]);

  if (isFetching || isFetchingLoginEnd) {
    return <Loader />;
  }
  if (initData?.authorizationRequestUrl) {
    window.location.href = initData.authorizationRequestUrl;
  }

  return null;
};

export default MoiLogin;
